import { useState, useEffect, useRef } from "react";
import {
  Button,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  CardContent,
  Card,
  Box,
  Modal,
  CircularProgress
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { _getApplicant } from "../../services/applicant.service";
import { getSelfEvalTest, submitMCQTest } from "../../services/dashboard";
import AlertDialog from "../../components/Dialog";
import { hideSidebar } from "../../redux/actions/sidebar";
import useFullscreen from "../../hooks/fullScreenWindow";
import { saveInterviewSession } from "../../redux/actions/candidate";
import { useScreenRecorder } from '../../contexts/ScreenRecorderContext';
// Modal popup styles
const modalPopUpStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 300,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: '8px',
  textAlign: 'center',
};

// Loader styles
const loaderStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.8)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1300,
  color: "#fff",
  fontSize: "1.5rem",
};


export const TestPage = ({
  saveInterviewSession,
  userData,
  auth: { user },
  hideSidebar
}) => {
  const navigate = useNavigate();
  const [answerSheet, setAnswerSheet] = useState([]);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [confirm, setConfirm] = useState(false);
  const { isFullScreen, enterFullScreen } = useFullscreen();
  const [isFaceDetected, setIsFaceDetected] = useState(true);
  const videoRef = useRef(null);
  const canvasRef = useRef(null); // Canvas to draw rectangles
  const [videoStream, setVideoStream] = useState(null);
  const [faceapi, setFaceApi] = useState(null);
  const [consentGiven, setConsentGiven] = useState(true); // Track consent
  const [consentModalOpen, setConsentModalOpen] = useState(false); // Consent modal state
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false); // Confirm dialog state
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true); // Disable submit initially
  const [showLoader, setShowLoader] = useState(false);
  const [questions, setQuestions] = useState([]); // Store fetched questions
  const [totalCount, setTotalCount] = useState(0); // Total questions count
  const [currentPage, setCurrentPage] = useState(0); // Current page for API calls
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Load More state
  const [preExist, setPreExist] = useState(false);
  const [skillData, setSkillData] = useState(null);
  const [applicant, setApplicant] = useState(null);
  const [submitData, setSubmitData] = useState(false);

  const isFirstRender = useRef(true); // Track initial render

  // Load face-api models dynamically
  useEffect(() => {
    const loadFaceApi = async () => {
      const faceapi = await import('face-api.js');
      await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
      await faceapi.nets.faceLandmark68Net.loadFromUri('/models');
      await faceapi.nets.faceRecognitionNet.loadFromUri('/models');
      setFaceApi(faceapi);
    };

    loadFaceApi();
  }, []);
  const containerRef = useRef(null); // Reference to the scroll container
  const debounceTimeout = useRef(null); // Store timeout reference
  // Show error modal with dynamic message
  const showErrorModal = (message) => {
    setErrorMessage(message);
    setOpenErrorModal(true);
  };

    const { 
        isRecording: isScreenRecording,
        startRecording: startScreenRecording,
        stopRecording: stopScreenRecording,
        recordingUrl: screenRecordingUrl,
        isEntireScreenSelected
    } = useScreenRecorder();
  
    useEffect(()=>{
      startScreenRecording("mcq");
    }, []);

  // Close error modal
  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
    enterFullScreen();
  };

  // Camera and audio permission handling after consent
  const getUserMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setVideoStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      showErrorModal("Camera and microphone access is required");
    }
  };

  useEffect(()=>{
    if (consentGiven) {
      // setConsentGiven(true);
      setShowLoader(true);
      setConsentModalOpen(false);
      getUserMedia(); // Request camera and microphone access after consent
    }
  },[consentGiven])

  // Handle consent acceptance
  const handleConsentAccept = () => {
    setConsentGiven(true);
    setShowLoader(true);
    setConsentModalOpen(false);
    getUserMedia(); // Request camera and microphone access after consent
  };

  // Draw rectangles around detected faces
  const drawFaceDetections = (detections) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear previous drawings
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;

    detections.forEach((detection) => {
      const { x, y, width, height } = detection.box;
      ctx.strokeStyle = 'red';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, width, height);
    });
  };

  // Facial detection and drawing bounding boxes
  useEffect(() => {
    let faceDetectionInterval;
    const detectFaces = async () => {
      if (videoRef.current && videoRef.current.readyState === 4 && faceapi) {
        const detections = await faceapi.detectAllFaces(
          videoRef.current,
          new faceapi.TinyFaceDetectorOptions({ inputSize: 160, scoreThreshold: 0.5 })
        );

        if (detections.length > 0) {
          drawFaceDetections(detections); // Draw rectangles around faces
        }

        if (detections.length > 1) {
          saveInterviewSession({ multiFaceDetected: true, apct_id: user.u_apct_id_fk })
          // showErrorModal("More than one face detected. Please ensure only one person is visible.");
        }

        setIsFaceDetected(detections.length === 1);
      }
    };

    if (faceapi && consentGiven) {
      faceDetectionInterval = setInterval(detectFaces, 2000); // Check every 2 seconds
    }

    return () => clearInterval(faceDetectionInterval);
  }, [faceapi, consentGiven]);

  // Fetch applicant and interview status
  useEffect(() => {
    const fetchData = async () => {
      try {
        // console.log(user);
        if (user && user.u_apct_id_fk) {
          // console.log("Helloooooo 22222");
          const applicantRes = await _getApplicant(user.u_apct_id_fk);
          // console.log(applicantRes?.data?.applicant?.apct_mcq_count);
          setSkillData(applicantRes?.data?.skills);
          setTotalCount(applicantRes?.data?.applicant?.apct_mcq_count);
          setApplicant(applicantRes?.data?.applicant);
          // console.log(appican);
        }
      } catch (error) {
        console.error(
          "Error fetching interview status or applicant data:",
          error
        );
      }
    };

    fetchData();
  }, [user]);

  // Show warning if no face or multiple faces are detected
  useEffect(() => {
    if (consentGiven && !isFaceDetected) {
      // showErrorModal("Face not detected. Please ensure only one face is visible.");
    }
  }, [isFaceDetected, consentGiven]);

  // Handle answer selection logic
  const onClickAnswer = (e, q_id, opt) => {
    let tempAnswerSheet = [...answerSheet];
    if (answerSheet.find((ans) => ans.q_id === q_id)) {
      if (answerSheet.find((ans) => ans.q_id === q_id && ans.answer === opt)) {
        tempAnswerSheet = answerSheet.filter((ans) => ans.q_id !== q_id);
      } else {
        tempAnswerSheet = answerSheet.map((ans) => {
          return ans.q_id === q_id ? { ...ans, answer: opt } : ans;
        });
      }
    } else {
      tempAnswerSheet.push({ q_id: q_id, answer: opt });
    }

    setAnswerSheet(tempAnswerSheet);
    setIsSubmitDisabled(tempAnswerSheet.length === 0); // Enable submit button if at least one answer is selected
  };

  // Show confirmation dialog before submitting
  const handleSubmitConfirm = () => {
    setSubmitConfirmOpen(true);
  };

  // Submit final data (original functionality)
  const submitFinalData = async () => {
    setSubmitData(true);
    let data = {
      skills: skillData,
      totalQues: questions?.length,
      answerSheetMCQ: answerSheet,
    };
    await submitMCQTest(data);
    // Stop media stream before navigating
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
    }
    setOpen(false);
    setConfirm(false);
    setSubmitData(false);
    stopScreenRecording();
    navigate('/candidate/dashboard', { replace: true });
    // window.location.reload();
  };


  // Function to fetch questions
  const fetchQuestions = async (page) => {
    try {
      // console.log(questions.length, totalCount);
      if (questions.length < totalCount) {
        setIsLoadingMore(true);
        const questionsData = await getSelfEvalTest({
          skills: skillData !== undefined && skillData !== null && skillData ? skillData : [],
          offset: page * 5, // Offset calculation based on page
          limit: 5, // Always fetch 5 questions at a time
          previousData:questions
        }, 'mcq');
        console.log(questionsData?.data);
        if (consentGiven && questionsData?.data?.length > 0) {
          console.log("Prexisst======>", "isPreExist" in questionsData?.data[0]);
          if ("isPreExist" in questionsData?.data[0]) {
            setPreExist(questionsData?.data[0].isPreExist);
          }
          setShowLoader(false); // Hide loader once test data is loaded
          setQuestions((prevQuestions) => [...prevQuestions, ...questionsData?.data]);
        }
      } else {
        setShowLoader(false);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle "Load More" button click
  const handleLoadMore = () => {
    fetchQuestions(currentPage); // Fetch next set of 5 questions
    setCurrentPage((prevPage) => prevPage + 1); // Increment the page index
  };

  useEffect(() => {
    if (confirm) {
      submitFinalData();
    }
  }, [confirm]);

  useEffect(() => {
    hideSidebar();
  }, []);

  useEffect(() => {
    const handleCopy = (e) => {
      e.preventDefault();
      // showErrorModal("Copying is not allowed");
    };

    const handleCut = (e) => {
      e.preventDefault();
      // showErrorModal("Cutting is not allowed");
    };

    const handlePaste = (e) => {
      e.preventDefault();
      // showErrorModal("Pasting is not allowed");
    };

    let mcqsPage = document.getElementById("mcqPage");
    mcqsPage.addEventListener('copy', handleCopy);
    mcqsPage.addEventListener('cut', handleCut);
    mcqsPage.addEventListener('paste', handlePaste);

    return () => {
      mcqsPage.removeEventListener('copy', handleCopy);
      mcqsPage.removeEventListener('cut', handleCut);
      mcqsPage.removeEventListener('paste', handlePaste);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && (e.key === 'c' || e.key === 'x' || e.key === 'v')) {
        e.preventDefault();
        let obj = { "c": "Copying", "x": "Cutting", "v": "Pasting" };
        // showErrorModal(`${obj[e.key]} is not allowed`);
      }
    };

    let mcqPage = document.getElementById("mcqPage");
    mcqPage.addEventListener('keydown', handleKeyDown);

    return () => {
      mcqPage.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      // showErrorModal("Action Not Allowed");
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveInterviewSession({ isFullScreen, apct_id: user.u_apct_id_fk })
        // showErrorModal("Switching Screen is not allowed");
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const handleMouseOut = (event) => {
      if (!event.relatedTarget) {
        saveInterviewSession({ isFullScreen, apct_id: user.u_apct_id_fk })
        // showErrorModal("You cannot leave the window");
      }
    };

    window.addEventListener('mouseout', handleMouseOut);

    return () => {
      window.removeEventListener('mouseout', handleMouseOut);
    };
  }, []);

  useEffect(() => {
    if (!isFullScreen) {
      saveInterviewSession({ isFullScreen, apct_id: user.u_apct_id_fk })
      showErrorModal("Full screen exit not allowed");
    }
  }, [isFullScreen]);
    useEffect(() => {
      // if (isFirstRender.current) {
      //   isFirstRender.current = false;
      //   return; // Skip the initial render
      // } 
      // console.log("====>", isEntireScreenSelected);
      let timeoutId;
      if (applicant && isEntireScreenSelected) {
        showErrorModal("Enable full screen to start");
        fetchQuestions(0); // Fetch the first 5 questions
        setCurrentPage(1); // Set the next page index
        return; // Exit early to prevent setting timeout
      } 
      timeoutId = setTimeout(() => {
        if (!isEntireScreenSelected) {
          if (videoStream) {
            videoStream.getTracks().forEach(track => track.stop());
          }
          alert("Entire screen share mandatory to process interview");
          navigate('/candidate/dashboard', { replace: true });
        }
      }, 10000);
    
      return () => clearTimeout(timeoutId); // Cleanup timeout on dependency change
      
    }, [isEntireScreenSelected]);

    useEffect(() => {
      const handleScroll = () => {
        if (
          window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10 &&
          questions.length < totalCount &&
          !isLoadingMore && !preExist
        ) {
          // Apply Debounce: Clear previous timeout and set a new one
          if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
          }
          debounceTimeout.current = setTimeout(() => {
            handleLoadMore();
          }, 300); // Adjust debounce time if needed
        }
      };
  
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
      };
    }, [questions, totalCount, currentPage, isLoadingMore, handleLoadMore]);

  return (
    <>
      {/* Consent Modal */}
      <Modal
        open={consentModalOpen}
        onClose={() => { }}
        aria-labelledby="consent-modal-title"
        aria-describedby="consent-modal-description"
      >
        <Box sx={modalPopUpStyle}>
          <Typography variant="h6" component="h2" id="consent-modal-title">
            Consent Required
          </Typography>
          <Typography sx={{ mt: 2 }}>
            This test requires access to your camera and microphone for verification. Do you consent?
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConsentAccept}
            sx={{ mt: 3 }}
          >
            Accept
          </Button>
        </Box>
      </Modal>

      {/* Error Modal */}
      <Modal
        open={openErrorModal}
        // open={false}
        onClose={() => { }}
        aria-labelledby="error-modal-title"
        aria-describedby="error-modal-description"
      >
        <Box sx={modalPopUpStyle}>
          <Typography variant="h6" component="h2" id="error-modal-title">
            Error
          </Typography>
          <Typography sx={{ mt: 2 }}>{errorMessage}</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCloseErrorModal}
            sx={{ mt: 3 }}
          >
            OK
          </Button>
        </Box>
      </Modal>

      {/* Submit Confirmation Dialog */}
      <Modal
        open={submitConfirmOpen}
        onClose={() => setSubmitConfirmOpen(false)}
        aria-labelledby="submit-confirm-modal-title"
        aria-describedby="submit-confirm-modal-description"
      >
        <Box sx={modalPopUpStyle}>
          <Typography variant="h6" component="h2" id="submit-confirm-modal-title">
            Confirm Submission
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Are you sure you want to submit your answers?
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={submitFinalData}
            sx={{ mt: 3 }}
          >
            Yes, Submit
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setSubmitConfirmOpen(false)}
            sx={{ mt: 2 }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>

       {/* Loader */}
       {showLoader && (
        <div style={loaderStyle}>
          <p>Your test is loading...</p>
        </div>
       )}
       {/** SubmitDataLoader */}
       {
        submitData && (
          <div style={loaderStyle}>
            <p>Submitting...</p>
          </div>
        )
       }

      <CardContent id="mcqPage">
        <Stack direction="column" alignItems="center" spacing={3}>
          <Box component="main" sx={{ width: "100%", position: "relative" }}>
            {/* Live video stream positioned at bottom-right */}
            <video
              ref={videoRef}
              autoPlay
              muted
              style={{
                position: "fixed",
                bottom: "450px", // Adjusted for buttons
                right: "20px",
                width: "200px",
                height: "150px",
                border: "1px solid black",
                borderRadius: "8px",
                zIndex: 9999,
                display: consentGiven ? 'block' : 'none' // Only show video if consent is given
              }}
            />
            {/* Canvas for drawing rectangles */}
            <canvas
              ref={canvasRef}
              style={{
                position: "fixed",
                bottom: "460px", // Adjusted for buttons
                right: "20px",
                width: "200px",
                height: "150px",
                zIndex: 10000, // Draw over the video
                pointerEvents: "none"
              }}
            />
            <AlertDialog
              open={open}
              setOpen={setOpen}
              title={title}
              setConfirm={setConfirm}
              content={content}
            />
            <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
              {questions && questions?.length > 0 && questions.map((item, index) => (
                <Card key={index} sx={{ mb: 2, userSelect: 'none' }}>
                  <CardContent>
                    <Typography variant="h6">
                      {index + 1}. {item.q_name}
                    </Typography>
                    <Stack direction="column">
                      {item?.options?.map((opt, idx) => (
                        <FormControlLabel
                          key={opt.a_id}
                          control={
                            <Checkbox
                              color="primary"
                              checked={answerSheet?.find(ans => ans.q_id === item.q_id && ans.answer === opt.a_label) ? true : false}
                              onChange={(e) => onClickAnswer(e, item.q_id, opt.a_label)}
                            />
                          }
                          label={opt.a_text}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
            {/* "Load More" Button */}
            {questions.length < totalCount && !preExist && isLoadingMore &&(
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          )}
          </Box>
        </Stack>

        {/* Submit and Back buttons */}
        {questions.length >= totalCount && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            {/* <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
              Back
            </Button> */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitConfirm}
              disabled={isSubmitDisabled}
            >
              Submit
            </Button>
          </Box>
        )}

      </CardContent>
    </>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  userData: state.auth.userData,
});

const mapDispatchToProps = {
  hideSidebar,
  saveInterviewSession
};

export default connect(mapStateToProps, mapDispatchToProps)(TestPage);
